import { useEffect, useMemo, useState } from "react";
import { 
    generateConversationsEvaluation, 
    getLastEvaluation 
} from "../services/analytics";

const CONVERSATION_LIMIT_OPTIONS = [10, 20, 30, 50, 100];
const metricsNameCollator = new Intl.Collator("es", {
    sensitivity: "base",
    numeric: true,
});

export const useAnalyticsChatsEvaluation = () => {
    const [conversationLimit, setConversationLimit] = useState(CONVERSATION_LIMIT_OPTIONS[0]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(null);
    const [evaluationData, setEvaluationData] = useState(null);
    const [lastGeneratedData, setLastGeneratedData] = useState(null);
    const [selectedFrequencyMetric, setSelectedFrequencyMetric] = useState("");

    useEffect(() => {
        const fetchLastEvaluation = async () => {
            const lastEvaluation = await getLastEvaluation();
            console.log("Last evaluation fetched:", lastEvaluation);
            if (lastEvaluation?.evaluation_data) {
                setEvaluationData(lastEvaluation.evaluation_data);
            }
            
            setLastGeneratedData({
                generated_date: lastEvaluation?.generated_date || null,
                status: lastEvaluation?.status || null,
            });

        };
        setIsGenerating(true);
        fetchLastEvaluation();
        setIsGenerating(false);
    }, []);

    const generalMetrics = useMemo(() => {
        if (!evaluationData?.general) return [];

        const metrics = [];

        // Extraer métricas del chatbot
        if (evaluationData.general.chatbot) {
            Object.entries(evaluationData.general.chatbot)
                .filter(
                    ([key, value]) =>
                        key !== "evaluated_conversations" && value?.avg !== undefined
                )
                .forEach(([key, value]) => {
                    metrics.push({
                        key,
                        name: value.name,
                        avg: value.avg,
                        description: value.description,
                        type: "chatbot",
                    });
                });
        }

        // Extraer métricas del operador
        if (evaluationData.general.operator) {
            Object.entries(evaluationData.general.operator)
                .filter(
                    ([key, value]) =>
                        key !== "evaluated_conversations" && value?.avg !== undefined
                )
                .forEach(([key, value]) => {
                    metrics.push({
                        key,
                        name: value.name,
                        avg: value.avg,
                        description: value.description,
                        type: "operator",
                    });
                });
        }

        return metrics.sort((leftMetric, rightMetric) =>
            metricsNameCollator.compare(
                leftMetric.name || leftMetric.key,
                rightMetric.name || rightMetric.key
            )
        );
    }, [evaluationData]);

    const scoreMetricOptions = useMemo(() => {
        if (!evaluationData?.details?.length) return [];

        const metricNamesByKey = {};

        // Construir mapeo de nombres desde chatbot
        if (evaluationData.general?.chatbot) {
            Object.entries(evaluationData.general.chatbot).forEach(([key, value]) => {
                if (value?.name) {
                    metricNamesByKey[key] = value.name;
                }
            });
        }

        // Construir mapeo de nombres desde operator
        if (evaluationData.general?.operator) {
            Object.entries(evaluationData.general.operator).forEach(([key, value]) => {
                if (value?.name) {
                    metricNamesByKey[key] = value.name;
                }
            });
        }

        const keys = new Set();
        evaluationData.details.forEach((detail) => {
            const score = detail?.score;
            if (!score) return;

            // Buscar métricas numéricas en chatbot
            if (typeof score.chatbot === "object" && score.chatbot !== null) {
                Object.entries(score.chatbot).forEach(([key, value]) => {
                    if (typeof value === "number") {
                        keys.add(key);
                    }
                });
            }

            // Buscar métricas numéricas en operator
            if (typeof score.operator === "object" && score.operator !== null) {
                Object.entries(score.operator).forEach(([key, value]) => {
                    if (typeof value === "number") {
                        keys.add(key);
                    }
                });
            }
        });

        return Array.from(keys)
            .map((key) => ({
                key,
                label: metricNamesByKey[key] || key.replaceAll("_", " "),
            }))
            .sort((leftMetric, rightMetric) =>
                metricsNameCollator.compare(leftMetric.label, rightMetric.label)
            );
    }, [evaluationData]);

    useEffect(() => {
        if (!scoreMetricOptions.length) return;
        const availableMetricKeys = scoreMetricOptions.map((metric) => metric.key);
        if (
            !selectedFrequencyMetric ||
            !availableMetricKeys.includes(selectedFrequencyMetric)
        ) {
            if (availableMetricKeys.includes("overall_score")) {
                setSelectedFrequencyMetric("overall_score");
            } else {
                setSelectedFrequencyMetric(availableMetricKeys[0]);
            }
        }
    }, [scoreMetricOptions, selectedFrequencyMetric]);

    const frequencyChartData = useMemo(() => {
        if (!evaluationData?.details?.length || !selectedFrequencyMetric) return null;

        const bins = new Array(10).fill(0);

        evaluationData.details.forEach((detail) => {
            const score = detail?.score;
            if (!score) return;

            let rawValue = null;

            // Buscar el valor en chatbot
            if (typeof score.chatbot === "object" && score.chatbot !== null) {
                rawValue = score.chatbot[selectedFrequencyMetric];
            }

            // Si no se encontró en chatbot, buscar en operator
            if (rawValue === null || rawValue === undefined) {
                if (typeof score.operator === "object" && score.operator !== null) {
                    rawValue = score.operator[selectedFrequencyMetric];
                }
            }

            if (typeof rawValue !== "number" || Number.isNaN(rawValue)) return;
            if (rawValue < 0 || rawValue > 10) return;

            const index = rawValue === 10 ? 9 : Math.floor(rawValue);
            bins[index] += 1;
        });

        const total = bins.reduce((acc, value) => acc + value, 0);
        let running = 0;
        const cumulativePercentage = bins.map((value) => {
            running += value;
            if (!total) return 0;
            return Number(((running / total) * 100).toFixed(2));
        });

        return {
            labels: [
                "0-1",
                "1-2",
                "2-3",
                "3-4",
                "4-5",
                "5-6",
                "6-7",
                "7-8",
                "8-9",
                "9-10",
            ],
            datasets: [
                {
                    label: "Cantidad de conversaciones",
                    data: bins,
                    backgroundColor: "#4A7CFF",
                    yAxisID: "y",
                    order: 2,
                },
                {
                    type: "line",
                    label: "Acumulado (%)",
                    data: cumulativePercentage,
                    borderColor: "#62aa04",
                    backgroundColor: "rgba(172, 255, 117, 0.11)",
                    yAxisID: "y1",
                    order: 1,
                    fill: "origin",
                    pointRadius: 3,
                    pointHoverRadius: 4,
                    tension: 0.2,
                },
            ],
        };
    }, [evaluationData, selectedFrequencyMetric]);

    const handleGenerateEvaluation = async () => {
        if (!conversationLimit) return;

        setIsGenerating(true);
        setGenerationProgress(null);
        const response = await generateConversationsEvaluation({
            conversationsLimit: Number(conversationLimit),
            onProgress: (progress) => setGenerationProgress(progress),
        });
        if (response?.evaluation_data) {
            setEvaluationData(response.evaluation_data);
            setLastGeneratedData(null);
        }
        setGenerationProgress(null);
        setIsGenerating(false);
    };

    return {
        conversationLimit,
        setConversationLimit,
        isGenerating,
        generationProgress,
        evaluationData,
        lastGeneratedData,
        selectedFrequencyMetric,
        setSelectedFrequencyMetric,
        generalMetrics,
        scoreMetricOptions,
        frequencyChartData,
        handleGenerateEvaluation,
        CONVERSATION_LIMIT_OPTIONS,
    };
};
